import { useEffect, useRef } from 'react';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';

/**
 * Proactively marks messages as read when viewing a conversation.
 * Calls markMessagesAsRead when messages load or when new messages arrive.
 */
export function useMarkMessagesAsRead(
  conversationId: string,
  messagesStatus: 'loading' | 'ready',
  messageCount: number,
  client: TenantedClient['resource']['chat']
): void {
  const lastMarkedCountRef = useRef<number>(0);
  const prevConversationIdRef = useRef<string>(conversationId);

  useEffect(() => {
    if (prevConversationIdRef.current !== conversationId) {
      prevConversationIdRef.current = conversationId;
      lastMarkedCountRef.current = 0;
    }
  }, [conversationId]);

  useEffect(() => {
    if (messagesStatus !== 'ready') {
      return;
    }
    if (messageCount === 0) {
      return;
    }
    if (messageCount > lastMarkedCountRef.current) {
      lastMarkedCountRef.current = messageCount;
      void client.markMessagesAsRead({ conversationId });
    }
  }, [messagesStatus, messageCount, conversationId, client]);
}
